import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useQuery } from '@apollo/client';
import { useTranslation } from 'react-i18next';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { useTheme } from 'styled-components';

// Components
import { Loader } from '../../../inputs/Loader';
import InputText from '../../../inputs/InputText';
import DropDownInput from '../../../inputs/DropDown/DropDownInput';
import { ArrowDirectionButton } from '../../../inputs/buttons/ArrowDirectionButton';

// Icons
import { AlertIcon } from '../../../../assets/icons/AlertIcon';

// Types
import { ModalProps, StartTimerFormData } from './types';
import { WorkOrderDirection } from '../../../../types/workOrder';
import { type PartialDirection } from '../../../inputs/buttons/ArrowDirectionButton/types';
import { UNSPECIFIC } from '../../../../types/utils';

// Hooks
import { useGpsToPK } from '../../../../hooks/useGpsToPK';
import { GET_ROUTE_SEGMENTS } from '../../../../gql/queries/surveillanceRoute';
import { resumeSeedFromSegments } from '../../../../utils/surveillanceRoute';
import type { RouteSegment } from '../../../../types/surveillanceRoute';
import {
  useRoadDirectionLabels,
  useRoadNamesById,
  useRoadPkRange,
} from '../../../../hooks/useRoadDirectionLabels';
import { roadPkRangeAFormulario } from '../../../../utils/pkValidation';
import { useSupportRoadCandidates } from '../../../../hooks/useSupportRoadCandidates';

// Styles — composición centrada espejo de PauseSurveillanceRouteModal.
import {
  ContainerForm,
  ContainerText,
  Title,
  Text,
  Section,
  PkFieldWrap,
  ButtonRow,
  ActionButton,
  ModalContainerStyled,
  ContainerLoader,
} from './styles';

// Schema
import { startTimerSchema } from './schema';
import { registerPk } from '../../../../utils/pkField';

/**
 * Margen para el prellenado por GPS.
 *
 * Eran 5 s «para no bloquear al Tablet», con el campo VACÍO mientras tanto — y
 * al vencer el plazo salta el error y el fix ya no llega nunca, así que el
 * operario acababa tecleando el PK a mano. Ahora el campo trae la semilla del
 * cierre anterior desde el primer momento, así que esperar más no se ve: solo
 * da margen a que el fix frío —el receptor lleva toda la pausa apagado— llegue
 * y la refine. Si el operario envía antes, el modal se desmonta y la respuesta
 * tardía no toca nada.
 */
const GPS_PREFILL_TIMEOUT_MS = 15_000;

const formatPkString = (km: number, meter: number): string =>
  `${km}+${meter.toString().padStart(3, '0')}`;

const parsePkString = (pk: string): { km: number; meter: number } | null => {
  const cleaned = pk.replace(/\s+/g, '');
  const match = cleaned.match(/^(\d+)\+(\d{1,4})$/);
  if (!match) return null;
  return { km: Number(match[1]), meter: Number(match[2]) };
};

// ═══════════════════════════════════════════════════════════════════
// COMPONENT — StartTimerModal (Timer iteration 2026-04-16)
// ═══════════════════════════════════════════════════════════════════

/**
 * StartTimerModal — Timer iteration 2026-04-16.
 *
 * Used to start a new timer (RouteSegment) on a surveillance route. The Tablet
 * supplies exactly DOS cosas: el sentido y el PK inicial.
 *
 * Direction uses ArrowDirectionButton (same as NewWorkOrderForm) with
 * ↓ Lleida, ↕ both, ↑ Girona, Sin especificar.
 *
 * Lo que este modal NO pide, y por qué — los dos por petición del cliente y por
 * la misma razón de fondo (un tramo se caracteriza por cómo ACABA, no por cómo
 * empieza):
 *   - **Carril** (retirado 2026-07-23) → se registra en `finishRouteSegment`.
 *   - **Descripción** (retirada 2026-08-10) → es el `pauseCauseDetail` del
 *     cierre, que es lo que pintan la tabla de Tramos y el informe.
 */
export const StartTimerModal = ({
  onClickClose,
  data,
}: ModalProps): JSX.Element => {
  const { t } = useTranslation(['pages', 'common', 'forms']);
  const theme = useTheme();
  const isLoading = data?.isLoading ?? false;
  // Cargando 2026-07-20: la mutation tarda 1,5-4s contra Atlas — sin esto el
  // modal quedaba con botones vivos (doble-submit posible) y sin feedback.
  const [isSubmitting, setIsSubmitting] = useState(false);

  /**
   * Semilla del tramo anterior cuando el llamador no la trae.
   *
   * El único camino para REANUDAR tras una pausa es el cronómetro del header, y
   * ahí no hay tramos cerrados a mano: solo se consulta el ACTIVO, que al
   * reanudar es `null`. Sin esto el campo salía vacío y había que esperar al
   * GPS, que además viene FRÍO — con el recorrido en `paused` la captura está
   * apagada, así que el receptor lleva toda la pausa sin trabajar.
   *
   * `cache-first` y no `network-only` a propósito: el loader del detalle ya
   * trae estos tramos (y los lee de caché sin cobertura), así que lo normal es
   * que la respuesta sea local e instantánea. Y `skip` en cuanto el llamador
   * trae la semilla puesta: no se pide lo que ya se tiene.
   */
  const { data: segmentsData } = useQuery<{ routeSegments: RouteSegment[] }>(
    GET_ROUTE_SEGMENTS,
    {
      variables: { surveillanceRouteId: data?.routeId },
      skip: !data?.routeId || !!data?.defaultPkInit,
      fetchPolicy: 'cache-first',
    }
  );

  const seedFromSegments = useMemo(
    () => resumeSeedFromSegments(segmentsData?.routeSegments),
    [segmentsData?.routeSegments]
  );

  const defaultPkInitString = useMemo(() => {
    if (!data?.defaultPkInit) return '';
    return formatPkString(data.defaultPkInit.km, data.defaultPkInit.meter);
  }, [data?.defaultPkInit]);

  // Etiquetas Lleida/Girona bajo las flechas ↓/↑ — mismo dato que el
  // formulario de creación de OT (`road.directions`), leído de caché por
  // roadId del recorrido.
  // Multi-carretera (2026-08-15): el tramo empieza en UNA de las carreteras del
  // recorrido, y quien la decide es EL GPS — el efecto de abajo la corrige en
  // cuanto hay posición. Lo de aquí es solo el valor de partida mientras llega:
  // la del tramo anterior si lo hay (reanudar una pausa no cambia de vía), y si
  // no, la primera. Con un recorrido de una sola carretera, lo de siempre.
  const routeRoadIds = data?.roadIds ?? [];
  const fallbackRoadId =
    data?.previousRoadId ?? seedFromSegments?.roadId ?? routeRoadIds[0];
  const [selectedRoadId, setSelectedRoadId] = useState(fallbackRoadId ?? '');
  /**
   * Vía de APOYO que ha propuesto el GPS (2026-09-23): la patrulla está en una
   * carretera de su concesión que el recorrido no declara —se ha ido a ayudar
   * al otro tramo—. Entra en el selector y, al crear el tramo, en el recorrido
   * (lo añade el servidor). Hasta hoy el GPS no la veía, el PK salía vacío y el
   * que se tecleaba del mojón acababa guardado en la vía equivocada.
   */
  const [supportRoadId, setSupportRoadId] = useState<string | null>(null);
  const supportCandidates = useSupportRoadCandidates(
    routeRoadIds,
    data?.concessionIds
  );
  // Leída al contestar el sensor, no al preguntarle: el catálogo de carreteras
  // puede llegar de la caché un instante después de montar el modal.
  const supportCandidatesRef = useRef(supportCandidates);
  supportCandidatesRef.current = supportCandidates;
  /**
   * El GPS ya ha contestado, o el operario ha tocado el selector: la semilla
   * deja de tener nada que decir.
   *
   * Hace falta porque la semilla llega ASÍNCRONA (sale de una consulta) y podría
   * aterrizar DESPUÉS del sensor. Es el mismo `mandaElSensor` del modal de
   * finalizar: quien tiene el dato de ahora manda, y lo heredado solo rellena
   * el hueco mientras tanto.
   */
  const seedSupersededRef = useRef(false);
  useEffect(() => {
    if (seedSupersededRef.current || !fallbackRoadId) return;
    // Forma funcional y `fallbackRoadId` como única dependencia: el efecto solo
    // reacciona a que llegue un valor de partida mejor, nunca a que el operario
    // mueva el selector — eso lo corta el guard de arriba.
    setSelectedRoadId((prev) => (prev === fallbackRoadId ? prev : fallbackRoadId));
  }, [fallbackRoadId]);

  // Etiquetas de sentido y extremos de la carretera SELECCIONADA: la C-25 y su
  // ramal no tienen los mismos, así que leerlos del recorrido sería mentir.
  const directionLabels = useRoadDirectionLabels(selectedRoadId);
  const roadPkRange = useRoadPkRange(selectedRoadId);

  // Opciones del selector de carretera. Solo se pinta con MÁS DE UNA: con una
  // sola no hay nada que elegir y el modal queda igual que siempre (mismo
  // criterio que la concesión única).
  // Con la vía de apoyo, al final: la del recorrido sigue a mano por si el
  // operario sabe que el GPS se equivoca.
  const optionRoadIds = supportRoadId
    ? [...routeRoadIds, supportRoadId]
    : routeRoadIds;
  const roadNames = useRoadNamesById(optionRoadIds);
  const roadOptions = useMemo(
    () =>
      optionRoadIds.map((id) => ({ value: id, label: roadNames[id] || id })),
    [optionRoadIds.join(','), roadNames]
  );
  const isSupportRoadSelected =
    !!selectedRoadId && !routeRoadIds.includes(selectedRoadId);

  const {
    control,
    register,
    handleSubmit,
    formState: { errors },
    reset,
    setValue,
    getFieldState,
  } = useForm<StartTimerFormData>({
    resolver: yupResolver(startTimerSchema),
    defaultValues: {
      direction: '',
      pkInit: defaultPkInitString,
    },
  });

  /**
   * Con qué carretera desempatar, leída en el momento en que contesta el
   * sensor y no cuando se le preguntó: la semilla llega asíncrona y puede
   * aterrizar entre las dos cosas.
   */
  const preferredRoadRef = useRef<string | undefined>(undefined);
  preferredRoadRef.current =
    data?.previousRoadId ?? seedFromSegments?.roadId ?? routeRoadIds[0];

  const resolveGpsPk = useGpsToPK();
  /**
   * 🔴 La cancelación y el guard de «ya se intentó» viven en refs, FUERA del
   * efecto — misma trampa documentada en `PauseSurveillanceRouteModal`.
   *
   * Las deps incluyen `data?.roadIds`, que es un ARRAY: llega con referencia
   * nueva en cada render, así que el efecto se re-ejecuta aunque el contenido
   * no cambie. Con `cancelled` declarado dentro, la limpieza de cada
   * re-ejecución mataba la espera al sensor. Y el reinicio en el efecto de
   * montaje es por `StrictMode`: sin él, el desmontaje simulado deja
   * `cancelledRef.current = true` y el prellenado nace muerto.
   */
  const prefillAttemptedRef = useRef(false);
  const cancelledRef = useRef(false);
  useEffect(() => {
    cancelledRef.current = false;
    prefillAttemptedRef.current = false;
    return () => {
      cancelledRef.current = true;
    };
  }, []);
  useEffect(() => {
    const roadIds = data?.roadIds ?? [];
    // 🔴 Sin `return` definitivo: si `roadIds` llega TARDE (el payload del modal
    // se arma con datos que aún se están cargando), el efecto se vuelve a
    // ejecutar y esta vez sí pregunta. Antes las deps eran `[]` y aquí se
    // acababa el prellenado para siempre, sin un solo aviso.
    if (roadIds.length === 0 || !navigator.geolocation) return;
    if (prefillAttemptedRef.current) return;
    prefillAttemptedRef.current = true;

    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        if (cancelledRef.current) return;
        const pk = await resolveGpsPk({
          lat: pos.coords.latitude,
          lng: pos.coords.longitude,
          roadIds,
          // En el tramo que una troncal y su ramal comparten, los candidatos
          // están a metros. Ahí gana la carretera del tramo ANTERIOR: reanudar
          // tras una pausa en el ramal y que el sistema te devuelva a la
          // troncal solo porque es la primera de la lista sería inventarse un
          // cambio de vía que no ha pasado. Sin tramo previo, la primera.
          preferredRoadId: preferredRoadRef.current,
          // Solo si NINGUNA del recorrido explica el punto (2026-09-23).
          otherRoadIds: supportCandidatesRef.current,
        });
        if (cancelledRef.current || !pk) return;
        // A partir de aquí manda el sensor: si la semilla llega después, calla.
        seedSupersededRef.current = true;
        if (pk.outsideRoute) setSupportRoadId(pk.roadId);
        // La carretera SÍ se corrige aunque el operario haya tecleado el PK:
        // son dos datos distintos, y el PK que él escribió lo escribió pensando
        // en la vía por la que va.
        setSelectedRoadId(pk.roadId);
        // No pisar lo que el operario ya haya tecleado mientras el GPS→PK
        // resolvía (2026-07-17 — visible sobre todo con red lenta).
        if (getFieldState('pkInit').isDirty) return;
        setValue('pkInit', formatPkString(pk.pkKm, pk.pkMeter), {
          shouldValidate: false,
          shouldDirty: false,
        });
      },
      () => {
        // GPS denegado o sin fix a tiempo — se queda la semilla del cierre
        // anterior, que para reanudar es la respuesta correcta.
      },
      {
        enableHighAccuracy: true,
        timeout: GPS_PREFILL_TIMEOUT_MS,
        maximumAge: 2000,
      }
    );
    // Sin `return`: la limpieza vive en el efecto de desmontaje de arriba. Un
    // re-render NO puede cancelar un prellenado en vuelo.
  }, [data?.roadIds, resolveGpsPk, getFieldState, setValue]);

  /**
   * El PK del último cierre, en cuanto se conoce.
   *
   * Es una semilla, no una respuesta: se retira sola en cuanto el sensor
   * contesta (`seedSupersededRef`) y nunca pisa lo que el operario haya
   * escrito.
   */
  useEffect(() => {
    if (!seedFromSegments || seedSupersededRef.current) return;
    if (getFieldState('pkInit').isDirty) return;
    setValue(
      'pkInit',
      formatPkString(seedFromSegments.pkInit.km, seedFromSegments.pkInit.meter),
      { shouldValidate: false, shouldDirty: false }
    );
  }, [seedFromSegments, getFieldState, setValue]);

  /**
   * Siembra los extremos de la carretera en el formulario para que el
   * `.test('pk-validation')` del schema tenga contra qué comparar.
   *
   * Va por `setValue` y no por `defaultValues` porque `useRoadPkRange` resuelve
   * ASÍNCRONO: si el recorrido se abre en frío, la carretera llega después de
   * montar el modal. Mismo gesto que hace `NewRoute` al elegir carretera.
   */
  useEffect(() => {
    const extremos = roadPkRangeAFormulario(roadPkRange);
    setValue('pkInitRoad', extremos.pkInitRoad);
    setValue('pkEndRoad', extremos.pkEndRoad);
  }, [roadPkRange, setValue]);

  const handleClose = useCallback(() => {
    reset();
    onClickClose();
  }, [reset, onClickClose]);

  const onSubmit = useCallback(
    async (formData: StartTimerFormData) => {
      if (!data?.onConfirm || isSubmitting) return;
      const pk = parsePkString(formData.pkInit);
      if (!pk) return;
      setIsSubmitting(true);
      try {
        await data.onConfirm({
          direction: formData.direction,
          pkInitKm: pk.km,
          pkInitMeter: pk.meter,
          // Multi-carretera: en qué carretera del recorrido empieza el tramo.
          // Solo se manda si el recorrido tiene más de una — con una sola el
          // backend ya la deduce y el payload queda igual que antes. Y SIEMPRE
          // que sea la vía de apoyo: sin ella el servidor no sabría añadirla.
          roadId:
            (data?.roadIds?.length ?? 0) > 1 || isSupportRoadSelected
              ? selectedRoadId
              : undefined,
        });
        // Fix 2026-07-03: cerrar el modal tras confirmar (antes solo reset() —
        // el modal quedaba abierto y vacío aunque el tramo se hubiera creado).
        handleClose();
      } finally {
        setIsSubmitting(false);
      }
    },
    // `selectedRoadId` es dependencia de verdad: sin él el callback se queda
    // con el cierre del primer render y manda SIEMPRE la primera carretera,
    // por mucho que el operario cambie el selector.
    [data, handleClose, isSubmitting, selectedRoadId, isSupportRoadSelected]
  );

  // Direction options mapping — same pattern as NewWorkOrderForm/Description.tsx
  const directionOptions: PartialDirection = {
    top: WorkOrderDirection.TOP,
    down: WorkOrderDirection.DOWN,
    both: WorkOrderDirection.BOTH,
    [UNSPECIFIC]: UNSPECIFIC,
  };

  return (
    <ModalContainerStyled
      // width 60 + composición centrada con icono, como sus hermanos del
      // ciclo de patrulla PauseTimerModal/ChangeDirectionModal (feedback
      // usuario 2026-07-14). El título va DENTRO — no se pasa `title`.
      width={60}
      onClickClose={handleClose}
      fill={theme.colors.black}
    >
      {isSubmitting && (
        <ContainerLoader>
          <Loader />
        </ContainerLoader>
      )}
      <ContainerForm onSubmit={handleSubmit(onSubmit)} isLoading={isSubmitting}>
        <AlertIcon />

        <ContainerText>
          <Title variant="semiBold">
            {t('surveillance.newSegmentModal.title')}
          </Title>
        </ContainerText>

        {/* Carretera — solo si el recorrido tiene más de una. Va ARRIBA porque
            manda sobre lo que hay debajo: las etiquetas de sentido y el rango
            de PK contra el que se valida salen de ELLA. */}
        {roadOptions.length > 1 && (
          <Section>
            <Text size="S">{t('forms:workOrder.road')}</Text>
            <DropDownInput
              id="start-segment-road-select"
              inputSize="L"
              typeDropDown="Default"
              placeholder={t('common:labels.select')}
              options={roadOptions}
              selectedOption={selectedRoadId}
              onChangeSelected={(option) => {
                // Elegida a mano: ni la semilla ni el arranque pueden pisarla.
                seedSupersededRef.current = true;
                setSelectedRoadId(option?.value ?? '');
              }}
            />
            {/* Decirlo: el tramo va a cambiar el recorrido, y el operario
                tiene que poder elegir otra vía si el GPS se equivoca. */}
            {isSupportRoadSelected && (
              <Text
                size="S"
                colorText="greyDark"
                data-testid="start-segment-support-road-notice"
              >
                {t('surveillance.newSegmentModal.outsideRouteRoad', {
                  road: roadNames[selectedRoadId] || selectedRoadId,
                })}
              </Text>
            )}
          </Section>
        )}

        {/* Sentido — ArrowDirectionButton (same as NewWorkOrderForm) */}
        <Section>
          <Text size="S">{t('common:workOrder.direction')}</Text>
          <Controller
            name="direction"
            control={control}
            render={({ field: { onChange } }) => (
              <ArrowDirectionButton
                directions={['down', 'both', 'top', UNSPECIFIC]}
                hasUnspecific
                name="direction"
                borderError={!!errors.direction}
                error={errors.direction?.message}
                directionLabels={directionLabels}
                onChangeDirection={(dir) => {
                  onChange(directionOptions[dir]);
                }}
              />
            )}
          />
        </Section>

        {/* Carril — D1 (2026-07-23): ya NO se pide al iniciar; se registra al
            CERRAR el tramo (modal Finalizar / Cambio de sentido). */}

        {/* PK Inicial — canonical pattern: InputText + register + yup matches */}
        <Section>
          <Text size="S">{t('surveillance.pkInit')}</Text>
          <PkFieldWrap>
            <InputText
              {...registerPk(register, setValue, 'pkInit')}
              typeInput="normal"
              placeholder={t('common:pkExample')}
              inputSize="S"
              borderError={!!errors.pkInit}
              error={
                errors.pkInit?.message ? t(errors.pkInit.message) : undefined
              }
            />
          </PkFieldWrap>
        </Section>

        {/* Sin campo de descripción (2026-08-10, petición del cliente): el tramo
            se describe UNA vez, al CERRARLO. Lo que se escribía aquí iba a
            `Order.description` de la Order gemela — invisible en la tabla de
            Tramos y en el informe, que pintan `pauseCauseDetail`, y sin forma de
            corregirlo después (`correctRouteSegment` no toca la descripción).
            Misma regla que el Carril desde 2026-07-23: se registra en el tramo
            que cierra, no en el que arranca. */}

        <ButtonRow>
          <ActionButton
            type="button"
            text={t('common:buttons.cancel')}
            variant="secondary"
            onClick={handleClose}
            disabled={isLoading || isSubmitting}
          />
          <ActionButton
            type="submit"
            text={t('surveillance.createSegment')}
            disabled={isLoading || isSubmitting}
          />
        </ButtonRow>
      </ContainerForm>
    </ModalContainerStyled>
  );
};

// Backwards-compat default export so existing SuperModal switch still finds it
export default StartTimerModal;

// Old name kept as named export until SuperModal is updated
export { StartTimerModal as NewSurveillanceSegmentModal };
